import React, { useState, useEffect, useMemo } from "react";
import {
  createPurchaseOrder,
  updatePurchaseOrder,
  searchParties,
  searchProduct,
  getSinglePurchaseOrder,
} from "../../services/services";
import { toast } from "react-toastify";
import { Link, useNavigate, useParams } from "react-router-dom";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import Loading from "../../components/loading";
import DataTable from "react-data-table-component";
import AsyncSearchSelect from "../sale/components/AsyncSearchSelect";

const PurchaseOrderPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const [errors, setErrors] = useState({});

  const [orderData, setOrderData] = useState({
    vendor: "",
    purchaseDate: "",
    items: [
      {
        productId: "",
        quantity: 1,
        purchasePrice: 0,
        salePrice: 0,
        batchCode: "",
      },
    ],
    otherExpenses: [],
    paidAmount: 0,
    paymentMode: "",
    notes: "",
    dueDate: "",
  });
  const [cachedOptions, setCachedOptions] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("lastPartySearchOptions")) || [];
    } catch {
      return [];
    }
  });
  const [cachedProductOptions, setCachedProductOptions] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("lastProductSearchOptions")) || [];
    } catch {
      return [];
    }
  });

  const loadProductOptions = async (input) => {
    if (!input || input.trim().length < 2) {
      return cachedProductOptions;
    }

    try {
      const res = await searchProduct({ search: input });

      const products = res?.data?.response || [];

      const options = products.map((p) => ({
        label: p.name,
        value: p._id,
        raw: p,
      }));

      // cache
      setCachedProductOptions(options);
      localStorage.setItem("lastProductSearchOptions", JSON.stringify(options));

      return options;
    } catch (err) {
      console.error("Product search failed:", err);
      return cachedProductOptions;
    }
  };
  const loadPartyOptions = async (input) => {
    // If input too short → return cached
    if (!input || input.trim().length < 2) {
      return cachedOptions;
    }

    try {
      const res = await searchParties({ search: input });

      const parties = res?.data?.response || [];

      const options = parties.map((p) => ({
        label: `${p.name}${p.mobile ? ` (${p.mobile})` : ""}`,
        value: p._id,
        raw: p,
      }));

      // cache
      setCachedOptions(options);
      localStorage.setItem("lastPartySearchOptions", JSON.stringify(options));

      return options;
    } catch (err) {
      console.error("Party search failed:", err);
      return cachedOptions;
    }
  };
  const clearSearchCache = () => {
    localStorage.removeItem("lastProductSearchOptions");
    localStorage.removeItem("lastPartySearchOptions");
  };

  useEffect(() => {
    const resizeObserverError = (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        const overlay = document.getElementById(
          "webpack-dev-server-client-overlay",
        );
        if (overlay) {
          overlay.style.display = "none";
        }
      }
    };

    window.addEventListener("error", resizeObserverError);
    return () => window.removeEventListener("error", resizeObserverError);
  }, []);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const res = await getSinglePurchaseOrder(id);

        const data = res.data.response;

        setOrderData({
          vendor: data.vendor?._id || "",
          vendorName: data.vendor?.name || "",
          purchaseDate: data.purchaseDate || "",
          dueDate: data.dueDate || "",
          notes: data.notes || "",
          paymentMode: data.paymentMode || "",
          paidAmount: data.paidAmount || 0,

          items: data.items.map((item) => ({
            productId: item.productId?._id || item.productId,
            itemName: item.name || item.productId?.name || "",
            quantity: item.quantity || 1,
            purchasePrice: item.purchasePrice || 0,
            salePrice: item.salePrice || 0,
            batchCode: item.batchCode || "",
          })),

          otherExpenses: data.otherExpenses || [],
        });
      } catch (error) {
        toast.error("Failed to load purchase order");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  const handleItemChange = (index, field, value) => {
    setOrderData((prev) => {
      const updatedItems = [...prev.items];

      let updatedValue = value;

      if (field === "quantity") {
        updatedValue = value.replace(/[^0-9]/g, "");
      }

      if (["purchasePrice", "salePrice"].includes(field)) {
        if (!/^\d*\.?\d*$/.test(value)) return prev;
      }

      updatedItems[index] = {
        ...updatedItems[index],
        [field]: updatedValue,
      };

      return { ...prev, items: updatedItems };
    });
  };

  const addItem = () => {
    setOrderData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          productId: "",
          quantity: 0,
          purchasePrice: 0,
          salePrice: 0,
          batchCode: "",
        },
      ],
    }));
  };

  const removeItem = (index) => {
    setOrderData((prev) => {
      if (prev.items.length === 1) {
        toast.warning("At least 1 item is required");
        return prev;
      }

      return {
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!orderData.vendor) {
      newErrors.vendor = "Vendor is required";
    }

    orderData.items.forEach((item, index) => {
      if (!item.productId) {
        newErrors[`product_${index}`] = "Product is required";
      }
      if (!item.quantity || Number(item.quantity) <= 0) {
        newErrors[`quantity_${index}`] = "Quantity must be greater than 0";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      // show first error in toast
      toast.error(Object.values(newErrors)[0]);

      // focus first error field
      const firstErrorKey = Object.keys(newErrors)[0];
      document.getElementById(firstErrorKey)?.focus();

      return;
    }

    setErrors({});

    try {
      const payload = {
        ...orderData,
        items: orderData.items.map((item) => ({
          productId: item.productId,
          name: item.itemName,
          quantity: Number(item.quantity),
          purchasePrice: Number(item.purchasePrice),
          salePrice: Number(item.salePrice),
          batchCode: item.batchCode || "",
        })),
      };
      const res = id
        ? await updatePurchaseOrder(id, payload)
        : await createPurchaseOrder(payload);

      toast.success(res.data?.message);
      navigate("/purchases/order/list");
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Error saving purchase order",
      );
    }
  };

  useEffect(() => {
    return () => {
      clearSearchCache();
    };
  }, []);

  const grandTotal = orderData.items.reduce(
    (sum, item) => sum + item.quantity * item.purchasePrice,
    0,
  );

  /* DATA TABLE COLUMNS */
  const columns = useMemo(
    () => [
      {
        name: "Product",
        grow: 2,
        cell: (row, index) => (
          <div className="py-2 w-100">
            {/* <ItemAutoSuggest
              selectedItemId={row.productId}
              selectedItemName={
                items.find((i) => i._id === row.productId)?.name || ""
              }
              onSelect={(item) => {
                setOrderData((prev) => {
                  const updated = [...prev.items];

                  updated[index] = {
                    ...updated[index],
                    productId: item._id,
                    purchasePrice: item.latestPurchasePrice || "",
                    salePrice: item.salePrice || "",
                  };

                  return { ...prev, items: updated };
                });

                setErrors((prev) => ({ ...prev, [`product_${index}`]: null }));
              }}
            /> */}
            <AsyncSearchSelect
              value={
                row.productId
                  ? {
                      label: row.itemName || "Selected Item",
                      value: row.productId,
                    }
                  : null
              }
              loadOptions={loadProductOptions}
              onChange={(selected) => {
                const product = selected?.raw;

                setOrderData((prev) => {
                  const updated = [...prev.items];

                  updated[index] = {
                    ...updated[index],
                    productId: product?._id || "",
                    itemName: product?.name || "",

                    // IMPORTANT FIXES
                    purchasePrice: product?.latestPurchasePrice || 0,
                    salePrice: product?.salePrice || 0,
                    quantity: 1,

                    // extra fields
                    stock: product?.stock || 0,
                    unit: product?.unit || "",
                    gstRate: product?.gstRate || 0,
                    taxType: product?.taxType || "exclusive",
                  };

                  return { ...prev, items: updated };
                });

                setErrors((prev) => ({ ...prev, [`product_${index}`]: null }));
              }}
              placeholder="Search product..."
              minInputLength={2}
              debounceWait={400}
              defaultOptions={cachedProductOptions}
              isClearable
            />
          </div>
        ),
      },
      {
        name: "Quantity",
        cell: (row, index) => (
          <input
            id={`quantity_${index}`}
            className={`form-control ${
              errors[`quantity_${index}`] ? "border-danger focus-danger" : ""
            }`}
            value={row.quantity}
            onChange={(e) => {
              handleItemChange(index, "quantity", e.target.value);
              setErrors((prev) => ({ ...prev, [`quantity_${index}`]: null }));
            }}
          />
        ),
      },
      {
        name: "Purchase Price (₹)",
        cell: (row, index) => (
          <input
            className="form-control"
            value={row.purchasePrice}
            onChange={(e) =>
              handleItemChange(index, "purchasePrice", e.target.value)
            }
          />
        ),
      },
      // {
      //   name: "(₹) Sale Price",
      //   width: "15rem",
      //   cell: (row, index) => (
      //     <input
      //       className="form-control"
      //       value={row.salePrice}
      //       onChange={(e) =>
      //         handleItemChange(index, "salePrice", e.target.value)
      //       }
      //     />
      //   ),
      // },
      {
        name: "Total (₹)",
        cell: (row, index) => (
          <input
            className="form-control"
            value={(
              (Number(row.quantity) || 0) * (Number(row.purchasePrice) || 0)
            ).toFixed(2)}
            readOnly
          />
        ),
      },
      {
        name: "Action",
        center: true,
        cell: (_, index) => (
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            onClick={() => removeItem(index)}
          >
            <i className="bi bi-trash"></i>
          </button>
        ),
      },
    ],
    [orderData.items, errors],
  );
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#0b2b6d",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#0b2b6d",
        color: "#ffffff",
        // fontWeight: "600",
        // fontSize: "14px",
        textTransform: "uppercase",
      },
    },
  };
  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div id="main">
          <div className="d-flex align-items-center mb-4">
            <Link to="/purchases/order/list" className="pe-3">
              <i className="bi bi-arrow-left" />
            </Link>
            <h2 className="custom-title mb-0">
              {id ? "Edit Purchase Order" : "Create Purchase Order"}
            </h2>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Vendor */}
            <div className="col-md-6 mb-3">
              <label className="form-label">
                Vendor <span className="text-danger">*</span>
              </label>

              <AsyncSearchSelect
                value={
                  orderData.vendor
                    ? {
                        label: orderData.vendorName || "Selected Vendor",
                        value: orderData.vendor,
                      }
                    : null
                }
                loadOptions={loadPartyOptions}
                onChange={(selected) => {
                  setOrderData((prev) => ({
                    ...prev,
                    vendor: selected?.value || "",
                    vendorName: selected?.label || "",
                  }));

                  setErrors((prev) => ({ ...prev, vendor: null }));
                }}
                placeholder="Search vendor..."
                minInputLength={2}
                debounceWait={500}
                isClearable
                defaultOptions={cachedOptions}
              />

              {errors.vendor && (
                <small className="text-danger">{errors.vendor}</small>
              )}
            </div>

            <div className="card">
              <div className="card-body p-0 rounded">
                {/* Items Table */}
                <DataTable
                  columns={columns}
                  data={orderData.items}
                  customStyles={customStyles}
                  noHeader
                  responsive
                />
              </div>
            </div>

            <div className="text-end mt-3 fw-semibold">
              Grand Total: ₹ {grandTotal.toFixed(2)}
            </div>

            <button
              type="button"
              className="btn btn-primary btn-sm mt-2"
              onClick={addItem}
            >
              + Add Row
            </button>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-custom"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {id ? "Update Order" : "Save Order"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default headerLayoutWrapper(PurchaseOrderPage);
